import React from "react";
import { normalize } from "../../utils/tourUtils";
import { colors, radius, spacing, transitions } from "../../utils/designSystem";

// Localized strings
const STRINGS = {
  en: {
    submit: "Submit",
    correct: "✓ Correct",
    incorrect: "✗ Incorrect",
    revealIn: (s) => `Reveal another letter in ${s}s...`,
    allRevealed: "All letters revealed",
    revealLetter: "Still need help? Tap to reveal a letter.",
    typeAnswer: "Type your answer",
  },
  es: {
    submit: "Enviar",
    correct: "✓ ¡Correcto!",
    incorrect: "✗ Incorrecto",
    revealIn: (s) => `Revelar otra letra en ${s}s...`,
    allRevealed: "Todas las letras reveladas",
    revealLetter: "¿Aún necesitas ayuda? Toca para revelar una letra.",
    typeAnswer: "Escribe tu respuesta",
  }
};

export function AnswerBox({ step, isCorrect, onSubmit, embedded = false, letterMask = [], onRevealLetter, showRevealLetter = false, lang = "en" }) {
  const t = STRINGS[lang] || STRINGS.en;
  const [value, setValue] = React.useState("");
  const [focus, setFocus] = React.useState(false);
  const [isIncorrect, setIsIncorrect] = React.useState(false);
  const [localCorrect, setLocalCorrect] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const norm = (s) => {
    try {
      if (typeof normalize === "function") return normalize(s);
    } catch {}
    return (s || "")
      .toString()
      .normalize?.("NFD").replace(/[\u0300-\u036f]/g, "") ?? s
      .replace(/[^a-z0-9]/gi, "")
      .toLowerCase();
  };

  const targetRaw =
    (step?.solution || (step?.acceptedAnswers?.[0] ?? "")).toString().toUpperCase().trim();
  const target = targetRaw.replace(/\s+/g, "");
  const targetLen = target.length || 1;

  const valueNorm = norm(value).toUpperCase();
  const overlayValue = React.useMemo(() => {
    const baseArr = valueNorm.padEnd(targetLen, " ").slice(0, targetLen).split("");
    (letterMask || []).forEach((v, i) => {
      if (v) baseArr[i] = target[i] || baseArr[i];
    });
    return baseArr.join("").replace(/\s+$/g, "");
  }, [valueNorm, letterMask, target, targetLen]);

  const letters = overlayValue.slice(0, targetLen).split("");
  const disabled = isCorrect === true || localCorrect === true;

  const allRevealed = React.useMemo(
    () => (letterMask || []).every(Boolean) && (letterMask || []).length >= targetLen,
    [letterMask, targetLen]
  );

  const handleChange = (e) => {
    setValue(e.target.value ?? "");
    setIsIncorrect(false);
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!onSubmit || disabled) return;

    const rawGuess = overlayValue || value;
    const correctNow = norm(rawGuess) === norm(targetRaw);

    // Always call onSubmit for both correct AND incorrect answers
    // This allows the streak counter to track wrong attempts
    onSubmit(rawGuess);

    if (correctNow) {
      setIsIncorrect(false);
      setLocalCorrect(true);
    } else {
      setIsIncorrect(true);
      setLocalCorrect(false);
    }
  };

  const handleRevealLetter = () => {
    if (cooldown > 0) return;
    onRevealLetter?.();
    setCooldown(30);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={embedded ? "w-full" : "max-w-md w-full"}
      autoComplete="off"
    >
      <style>{`
        .boxes { display: grid; grid-template-columns: repeat(${Math.max(targetLen,1)}, minmax(28px, 1fr)); gap: ${spacing[2]}; margin-bottom: ${spacing[3]}; }
        .box { height: 44px; display: grid; place-items: center; border: 1px solid ${colors.neutral[300]}; border-radius: ${radius.sm}; font-weight: 700; background: ${colors.neutral.white}; }
        .box--cursor { outline: 2px solid ${colors.neutral[400]}; outline-offset: 2px; }
        .box--done { background: ${colors.accent.light}20; border-color: ${colors.accent.light}; }
        .box--locked { background: ${colors.success.bg}; border-color: ${colors.success.main}; }
        .visually-hidden { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
        .submit-full { width: 100%; border-radius: ${radius.sm}; padding: ${spacing[3]}; font-weight: 700; font-size: 1rem; transition: ${transitions.default}; }
        .black-pill-btn {
          background: ${colors.neutral.black};
          color: ${colors.neutral.white};
          border: none;
          border-radius: ${radius.full};
          padding: ${spacing[3]} ${spacing[6]};
          font-weight: 600;
          box-shadow: 0 3px 8px rgba(0,0,0,0.18);
          transition: ${transitions.default};
          cursor: pointer;
        }
        .black-pill-btn:hover { background: ${colors.neutral[800]}; transform: translateY(-1px); }
        .black-pill-btn:active { transform: translateY(0); }
      `}</style>

      <div
        className="boxes"
        onClick={() => document.getElementById(`answer-input-${step?.id}`)?.focus()}
        role="group"
        aria-label="Answer input"
      >
        {Array.from({ length: targetLen }).map((_, i) => {
          const ch = letters[i] || "";
          const cursorIndex =
            valueNorm.length < targetLen ? valueNorm.length : targetLen - 1;
          const isCursor = i === cursorIndex && !disabled && focus;
          const isFilled = Boolean(ch);
          const classes = [
            "box",
            disabled ? "box--locked" : isFilled ? "box--done" : "",
            isCursor ? "box--cursor" : "",
          ]
            .join(" ")
            .trim();
          return (
            <div key={i} className={classes} aria-live="polite">
              {ch || ""}
            </div>
          );
        })}
      </div>

      <input
        id={`answer-input-${step?.id}`}
        type="text"
        inputMode="text"
        className="visually-hidden"
        value={value}
        onChange={handleChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-label={t.typeAnswer}
      />

      {}
      {showRevealLetter && (
        <div className="mb-2 flex gap-2 items-center">
          <button
            type="button"
            className="text-sm underline text-slate-600 hover:text-slate-800 font-normal disabled:opacity-50"
            onClick={handleRevealLetter}
            disabled={disabled || allRevealed || cooldown > 0}
          >
            {cooldown > 0
              ? t.revealIn(cooldown)
              : allRevealed
              ? t.allRevealed
              : t.revealLetter}
          </button>
        </div>
      )}

      {}
      <div>
        {(isCorrect || localCorrect) ? (
          <div
            className="submit-full text-white text-center"
            style={{ background: colors.success.main }}
          >
            {t.correct}
          </div>
        ) : isIncorrect ? (
          <div
            className="submit-full text-white text-center"
            style={{ background: colors.error.main }}
          >
            {t.incorrect}
          </div>
        ) : (
          <button
            type="submit"
            disabled={disabled}
            className="submit-full black-pill-btn"
          >
            {t.submit}
          </button>
        )}
      </div>
    </form>
  );
}
